import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { ConnectionState, IConnectionStatus, IRetryPolicySettings, IServer } from '../types';

interface IInternalRetryPolicySettings extends IRetryPolicySettings {
    enabled: boolean;
    maxRetries: number;
    stepPattern: (currentRetryAttempt: number) => number;
}

const DEFAULT_POLICY_SETTINGS: IInternalRetryPolicySettings = {
    enabled: false,
    maxRetries: 5,
    stepPattern: (currentRetryAttempt) => {
        return (currentRetryAttempt * currentRetryAttempt) * 1000;
    }
};

export class RetryPolicy {
    public isRetrying: boolean;
    private currentRetryAttempt: number;
    private totalRetryAttempts: number;
    private retryPolicySettings: IInternalRetryPolicySettings;

    constructor(private readonly server: IServer, error$: Observable<Error>, private readonly connectionStatus$: Subject<IConnectionStatus>, retryPolicySettings?: IRetryPolicySettings) {
        this.server = server;
        this.isRetrying = false;
        this.currentRetryAttempt = 0;
        this.totalRetryAttempts = 0;
        this.retryPolicySettings = Object.assign(DEFAULT_POLICY_SETTINGS, retryPolicySettings);

        if (this.retryPolicySettings.enabled) {
            this.initialiseStreamsForRetryPolicy(error$, connectionStatus$);
        }
    }

    private reset() {
        this.currentRetryAttempt = 0;
        this.isRetrying = false;
    }

    private async doRetry() {
        const { maxRetries, stepPattern } = this.retryPolicySettings;
        this.currentRetryAttempt++;
        this.totalRetryAttempts++;
        if (this.currentRetryAttempt > maxRetries) {
            this.log('max attempts exceeded; retry circuit broken');
            this.connectionStatus$.next({ STATE: ConnectionState.RETRY_CIRCUIT_BROKEN });
            this.reset();
            return;
        }

        try {
            this.log(`retrying; attempt ${this.currentRetryAttempt} of ${maxRetries}`);
            this.isRetrying = true;

            await this.server.disconnect(true);
            await this.server.connect({
              maxRetries,
              retryAttempt: this.totalRetryAttempts
            });
            this.reset();
        } catch (err) {
            this.logError('retry failed', err);
            setTimeout(() => this.doRetry(), stepPattern(this.currentRetryAttempt));
        }
    }

    private initialiseStreamsForRetryPolicy(error$: Observable<Error>, connectionStatus$: Observable<IConnectionStatus>) {
        error$.subscribe(() => {
            if (!this.isRetrying) {
                this.log('triggered by error$');
                this.doRetry();
            }
        });

        connectionStatus$.subscribe((payload) => {
            if (payload.STATE === ConnectionState.CONNECTION_LOST && !this.isRetrying) {
                this.log('triggered by connectionStatus$');
                this.doRetry();
            }
        });
    }

    private log(message: any): void {
        this.server.logger().log(this.buildLogMessage(message));
    }

    private logError(message: any, error?: any): void {
        this.server.logger().error(this.buildLogMessage(message), error);
    }

    private buildLogMessage(message: any): string {
        const prefix = `RETRY[url=${this.server.url()}]: `;
        if (typeof message === 'string') {
            return `${prefix}${message}`;
        }
        return `${prefix}${JSON.stringify(message)}`;
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/app-modules/server-communications/utilities/retry-policy.ts